import { API, ApiPrefixes } from "../../api";
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";

const BASE_URL = ApiPrefixes["adminCompanyPrefixesSettings"];

const prefixesAdapter = createEntityAdapter();
const initialState = prefixesAdapter.getInitialState();

export const companyPrefixExtendedApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getPrefixes: builder.query({
      query: () => `${BASE_URL}?guard=staff`,
      providesTags: (result, error, arg) => {
        if (!result?.ids) return [{ type: "Prefix", id: "LIST" }];
        return [
          { type: "Prefix", id: "LIST" },
          ...result.ids.map((id) => ({ type: "Prefix", id: String(id) })),
        ];
      },
      transformResponse: (responseData) => {
        // Backend should return single prefix object for active branch
        // Handle both single object and array for backward compatibility
        const data = responseData.data;
        if (!data) {
          return initialState;
        }
        
        // If it's already an array, use it as is
        if (Array.isArray(data)) {
          return prefixesAdapter.setAll(initialState, data);
        }
        
        // If it's a single object, convert to array
        if (data && typeof data === 'object' && data.id) {
          return prefixesAdapter.setAll(initialState, [data]);
        }
        
        return initialState;
      },
    }),
    addPrefix: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/create_prefix?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [{ type: "Prefix", id: "LIST" }],
    }),
    editPrefix: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/${body.prefixId}/update_prefix?guard=staff`,
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Prefix", id: arg.prefixId },
      ],
    }),
    deletePrefix: builder.mutation({
      query: (prefixId) => ({
        url: `${BASE_URL}/${prefixId}/delete?guard=staff`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Prefix", id: String(arg) },
      ],
    }),
  }),
});

const selectPrefixesResult =
  companyPrefixExtendedApi.endpoints.getPrefixes.select();

const selectPrefixesData = createSelector(
  selectPrefixesResult,
  (result) => result.data
);

export const { selectAll: selectAllPrefixes, selectById: selectPrefixById } =
  prefixesAdapter.getSelectors(
    (state) => selectPrefixesData(state) ?? initialState
  );

export const {
  useGetPrefixesQuery,
  useAddPrefixMutation,
  useEditPrefixMutation,
  useDeletePrefixMutation,
} = companyPrefixExtendedApi;
